// React and third-party libraries
import * as React from "react";
import styled from "styled-components";
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Dot } from "lucide-react";
import { Link } from "gatsby";

// Local imports (styles, constants, etc.)
import { colors, media } from "../../../style/constants";
import { sectionPadding } from "../../../style/layout";

// Translation
import { useTranslation } from "react-i18next";

/* Styled Components */

// Section wrapper with background and padding
const SectionWrapper = styled.section`
  position: relative;
  overflow: hidden;
  background-color: ${colors.darkBg};
  ${sectionPadding}
`;

// Central container with max width
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

// Text section with animated reveal
const TextContent = styled(motion.div)`
  margin: 0 auto 2rem;
  max-width: 48rem;

  @media (min-width: ${media.tabletSm}px) {
    margin: 0 auto 4rem;
  }
`;

// Main heading
const Heading = styled(motion.h2)`
  font-weight: bold;
  text-align: center;
  margin-bottom: 1rem;
  font-size: 1.75rem;
  line-height: 2.25rem;

  @media (min-width: ${media.tabletSm}px) {
    font-size: 1.875rem;
    line-height: 2.25rem;
  }
`;

// Supporting paragraph
const Paragraph = styled(motion.p)`
  font-weight: 500;
`;

// Grid layout for cards
const GridLayout = styled(motion.div)`
  display: grid;
  justify-content: center;
  gap: 1rem;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  
  @media (min-width: ${media.tabletSm}px) {
    gap: 2rem;
    grid-template-columns: repeat(auto-fit, minmax(0, 583px));
  }
`;

// Card wrapper
const CardContainer = styled(motion.div)`
  background: ${colors.card.default};
  border: 1px solid ${colors.border.default};
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  transform: translateY(20px);
  transition: border 0.6s ease, transform 0.3s ease;

  &:hover {
    border: 1px solid rgba(42, 163, 223, 0.4);
  }
`;

// Card content block
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1rem;
  height: 100%;
`;

// Card title
const CardTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  font-size: 1.125rem;
  line-height: 1.5rem;
  color: ${colors.white};
  font-weight: bold;
  margin-bottom: 1rem;
  
  @media (min-width: ${media.tabletSm}px) {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
`;

const ChevronIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;

  @media (min-width: ${media.tabletSm}px) {
    width: 20px;
    height: 20px;
  }
`
// Card description text
const Text = styled.p`
  text-align: left;
  color: ${colors.lightGrey};
  font-size: 1rem;
  margin-bottom: 1.25rem;
`;

// Use case label
const ListTitle = styled.p`
  text-align: left;
  color: ${colors.lightGrey};
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
`;

// Wrapper for use case list
const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

// Individual use case item
const ListItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 1rem;  
  color: ${colors.lightGrey};
  transition: all 0.3s ease-in-out;
  margin-bottom: 0.2rem;

  &:hover {
    color: ${colors.primary};
    transform: translateX(4px);
  }
`;

// Icon for hover animation
const IconContainer = styled(motion.div)`
  display: inline-flex;
  transition: transform 0.3s ease;
`;

// Styled link with animation
const StyledLink = styled(Link)`
  text-align: left;
  color: ${colors.primary};
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  gap: 4px;
  margin-top: auto;

  &:hover {
    ${IconContainer} {
      transform: translateX(4px);
    }
  }
`;


/**
 * SolutionsSection Component - Products Page
 * Lists solutions with their use cases and details
 * 
 * @param {string} title - Section title
 * @param {string} subtitle - Supporting paragraph
 * @param {Array} solutionsList - Array of solution objects including title, subtitle, link, and useCases
 */
const SolutionsSection = ({
  title,
  subtitle,
  solutionsList
}) => {
  const { t } = useTranslation(); // Explicitly specify the namespace

  return (
    <SectionWrapper id="solutions-section">
      <Container>
        {/* Section Header */}
        <TextContent
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: "all" }}
        >
          <Heading
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
            }}
          >
            {title}
          </Heading>
          <Paragraph
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.2 } }
            }}
          >
            {subtitle}
          </Paragraph>
        </TextContent>

        {/* Solution Cards */}
        <GridLayout
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.25 } }
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {solutionsList?.map(({ title, subtitle, useCases, link }, index) => (
            <CardContainer
              key={`solution-${index}`}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
              }}
            >
              <CardBody>
                <CardTitle>
                  <ChevronIconContainer>
                    <ChevronRight color={colors.primary} />
                  </ChevronIconContainer>
                  {title}
                </CardTitle>
                <Text>{subtitle}</Text>
                {useCases?.length > 0 && (
                  <>
                    <ListTitle>{t("solutionsSection.useCases")}</ListTitle>
                    <FlexColumn>
                      {useCases.map(({ link, name }, caseIndex) => (
                        <ListItem
                          key={`usecase-${caseIndex}`}
                          to={link}
                          aria-label={`${t("callToAction.learnMoreAbout")} ${name}`}
                          state={{
                            prevPath:
                              typeof window !== "undefined"
                                ? `${window.location.pathname}#solutions-section`
                                : "/"
                          }}
                        >
                          <Dot size={30} color={colors.primary} /> {name}
                        </ListItem>
                      ))}
                    </FlexColumn>
                  </>
                )}
                <StyledLink
                  to={link}
                  aria-label={`${t("callToAction.learnMoreAbout")} ${title}`}
                >
                  {t("solutionsSection.learnMore")}
                  <IconContainer>
                    <ArrowRight size={16} />
                  </IconContainer>
                </StyledLink>
              </CardBody>
            </CardContainer>
          ))}
        </GridLayout>
      </Container>
    </SectionWrapper>
  );
};

export default SolutionsSection;
